import {
  Button, Flex, Result as AntResult
} from "antd";
import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { appRoutes } from "../../constants/routes";
import Logo from "../../components/Logo";
import useAppointments from "../../hooks/useAppointments";
import { downloadICSFile } from "../../utils/icsGenerator";

const Result = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { appointments } = useAppointments();

  const isSuccessPage = useMemo(() => {
    return location.pathname === appRoutes.success || location.pathname === "/success";
  }, [location]);

  const isCancelPage = useMemo(() => {
    return location.pathname === appRoutes.cancel;
  }, [location]);

  // Get appointment from route state or URL query parameters
  const appointmentFromState = useMemo(() => {
    return location.state?.appointment;
  }, [location.state]);

  // Get appointment ID from URL query parameters (for backward compatibility)
  const appointmentIdFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("appointment_id");
  }, [location.search]);

  // Get appointment for calendar download
  const appointmentForDownload = useMemo(() => {
    if (!isSuccessPage) return null;

    // First try to use appointment from route state
    if (appointmentFromState) {
      return appointmentFromState;
    }

    // If no state, try to find appointment by ID from URL query params
    if (appointmentIdFromUrl && appointments.length) {
      const found = appointments.find(apt => apt.id === appointmentIdFromUrl);
      if (found) return found;
    }

    // Fallback: look for today's appointment
    if (appointments.length) {
      const today = dayjs().format("YYYY-MM-DD");
      const todaysAppointment = appointments.find(appointment => dayjs(appointment.start_at).format("YYYY-MM-DD") === today);
      if (todaysAppointment) return todaysAppointment;
    }

    return null;
  }, [appointments, isSuccessPage, appointmentFromState, appointmentIdFromUrl]);

  const handleDownloadCalendar = () => {
    if (appointmentForDownload) {
      downloadICSFile(appointmentForDownload);
    }
  };

  const title = () => {
    if (isSuccessPage) {
      return t("successPage.title");
    }
    if (isCancelPage) {
      return t("cancelPage.title");
    }
    return "";
  };

  const subTitle = () => {
    if (isSuccessPage) {
      return t("successPage.text");
    }
    if (isCancelPage) {
      return t("cancelPage.text");
    }
    return "";
  };

  return (
    <Flex
      vertical
      justify="center"
      align="center"
      style={{
        height: "100vh",
        width: "100vw"
      }}
    >
      <span style={{
        position: "absolute",
        top: 56,
        left: "50%",
        transform: "translateX(-50%)"
      }}
      >
        <Logo/>
      </span>
      <AntResult
        status={isSuccessPage ? "success" : "error"}
        title={<strong>{title()}</strong>}
        subTitle={subTitle()}
        extra={[
          <Link to={appRoutes.appointments} key="exit">
            <Button type="primary">
              {t("successPage.return")}
            </Button>
          </Link>,
          ...(isSuccessPage && appointmentForDownload ? [
            <Button
              key="download"
              onClick={handleDownloadCalendar}
              style={{ marginLeft: 8 }}
            >
              {t("successPage.downloadCalendar")}
            </Button>
          ] : [])
        ]}
        style={{ maxWidth: 700 }}
      />
    </Flex>
  );
};

export default Result;
